import { createContext } from 'react';
import { FeatureFlag, type EFeatureFlag } from '@/lib/feature-flags/keys';

export interface FeatureFlagContextValue {
  enabled: Record<EFeatureFlag, boolean>;
  restricted: EFeatureFlag[];
  isLoading: boolean;
  /**
   * True only once the server's effective-flag map has actually been received.
   * Every flag reads `false` before that and after a failed fetch, so any
   * consumer that takes an irreversible decision on a disabled flag (blocking a
   * whole session, for example) must wait for this rather than for `isLoading`.
   */
  isResolved: boolean;
  /**
   * True when the fetch failed outright. Distinguishes "the server says you
   * are not entitled to this" from "we could not reach the server to ask" —
   * the two look identical in `enabled`, and telling a user to contact their
   * administrator over a network blip sends them to the wrong person.
   */
  isError: boolean;
  refetch: () => void;
}

const buildDefaultEnabled = (): Record<EFeatureFlag, boolean> => {
  const out = {} as Record<EFeatureFlag, boolean>;
  for (const key of Object.values(FeatureFlag)) {
    out[key] = false;
  }
  return out;
};

export const DEFAULT_FEATURE_FLAG_VALUE: FeatureFlagContextValue = {
  enabled: buildDefaultEnabled(),
  restricted: [],
  isLoading: false,
  isResolved: false,
  isError: false,
  refetch: () => undefined,
};

export const FeatureFlagContext = createContext<FeatureFlagContextValue>(DEFAULT_FEATURE_FLAG_VALUE);
